import React from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import type { ChangeExtraValue, ChangeFormField, ChangePeriodMode } from '../../../types/changes';
import { formatCurrencyInputDisplay, normalizeCurrencyInput } from '../../../utils/currencyInput';
import {
  canonicalChangeFieldType,
  emptyExtraValue,
  storedYesNoValue,
  yesNoChoices,
} from '../changeFormSchema';
import { CHANGE_FIELD_LIMITS } from '../changeValidation';
import SearchableSelect from './SearchableSelect';

interface Props {
  field: ChangeFormField;
  value: ChangeExtraValue;
  onChange: (value: ChangeExtraValue) => void;
  disabled?: boolean;
}

const periodMode = (field: ChangeFormField): ChangePeriodMode =>
  field.period_mode || 'range';

const ChangeExtraFieldInput: React.FC<Props> = ({ field, value, onChange, disabled = false }) => {
  const fieldType = canonicalChangeFieldType(field.field_type);
  const raw = value ?? emptyExtraValue(fieldType);
  if (fieldType === 'period' || fieldType === 'period_datetime') {
    const period = typeof raw === 'object' && raw && 'start' in raw ? raw : { start: '', end: '' };
    const inputType = fieldType === 'period_datetime' ? 'datetime-local' : 'date';
    const mode = periodMode(field);
    const showStart = mode !== 'end';
    const showEnd = mode !== 'start';
    return (
      <Row className="g-2">
        {showStart ? (
          <Col>
            <Form.Control
              type={inputType}
              aria-label={`${field.label} início`}
              value={period.start}
              disabled={disabled}
              onChange={(e) => onChange({ ...period, start: e.target.value })}
            />
          </Col>
        ) : null}
        {showEnd ? (
          <Col>
            <Form.Control
              type={inputType}
              aria-label={`${field.label} fim`}
              value={period.end}
              disabled={disabled}
              onChange={(e) => onChange({ ...period, end: e.target.value })}
            />
          </Col>
        ) : null}
      </Row>
    );
  }
  if (fieldType === 'currency') {
    const money =
      typeof raw === 'object' && raw && 'currency' in raw
        ? raw
        : { currency: 'BRL', value: '' };
    const currency = money.currency || 'BRL';
    return (
      <Row className="g-2 align-items-center">
        <Col xs="auto">
          <Form.Select
            aria-label={`${field.label} moeda`}
            value={currency}
            disabled={disabled}
            onChange={(e) => onChange({ ...money, currency: e.target.value })}
          >
            <option value="BRL">R$ (real)</option>
            <option value="EUR">€ (euro)</option>
            <option value="USD">US$ (dólar)</option>
          </Form.Select>
        </Col>
        <Col>
          <Form.Control
            type="text"
            inputMode="decimal"
            aria-label={field.label}
            placeholder={currency === 'BRL' ? '0,00' : '0.00'}
            value={formatCurrencyInputDisplay(money.value || '', currency)}
            disabled={disabled}
            onChange={(e) => {
              const normalized = normalizeCurrencyInput(e.target.value);
              if (normalized === null) return;
              onChange({ ...money, value: normalized });
            }}
          />
        </Col>
      </Row>
    );
  }
  if (fieldType === 'yes_no') {
    const selected = storedYesNoValue(field, typeof raw === 'string' ? raw : '');
    return (
      <div className="d-flex flex-wrap gap-3">
        {yesNoChoices(field).map((choice) => (
          <Form.Check
            key={choice.value}
            type="radio"
            id={`${field.id}-${choice.value}`}
            name={`yes-no-${field.id}`}
            label={choice.label}
            checked={selected === choice.value}
            disabled={disabled}
            onChange={() => onChange(choice.value)}
          />
        ))}
      </div>
    );
  }
  if (fieldType === 'list') {
    return (
      <SearchableSelect
        ariaLabel={field.label}
        value={typeof raw === 'string' ? raw : ''}
        disabled={disabled}
        searchEnabled={Boolean(field.select_search_enabled)}
        options={(field.options || []).map((option) => ({ value: option, label: option }))}
        onChange={(next) => onChange(String(next))}
      />
    );
  }
  if (fieldType === 'integer') {
    const text = typeof raw === 'string' ? raw : '';
    return (
      <Form.Control
        type="text"
        inputMode="numeric"
        aria-label={field.label}
        value={text}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          if (next === '' || next === '-' || /^-?\d+$/.test(next)) onChange(next);
        }}
      />
    );
  }
  if (fieldType === 'decimal') {
    const text = typeof raw === 'string' ? raw : '';
    return (
      <Form.Control
        type="text"
        inputMode="decimal"
        aria-label={field.label}
        value={text}
        disabled={disabled}
        onChange={(e) => {
          const next = e.target.value;
          if (next === '' || next === '-' || /^-?\d*([.,]\d*)?$/.test(next)) onChange(next);
        }}
      />
    );
  }
  const inputType =
    fieldType === 'date' ? 'date' : fieldType === 'datetime' ? 'datetime-local' : 'text';
  return (
    <Form.Control
      type={inputType}
      as={fieldType === 'text_long' ? 'textarea' : undefined}
      rows={fieldType === 'text_long' ? 3 : undefined}
      aria-label={field.label}
      value={typeof raw === 'string' ? raw : ''}
      maxLength={fieldType === 'text_short' ? CHANGE_FIELD_LIMITS.title : undefined}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

export default ChangeExtraFieldInput;
